import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DateTimePicker, {
  type DateTimeValue,
} from "../../components/DateTimePicker";
import ItineraryLocationSearchInput from "../../components/ItineraryLocationSearchInput";
import {
  isLocationInSalzburg,
  type LocationGeoData,
} from "../../utils/salzburgLocation";
import type { BookingSearchData } from "../../pages/Booking";
import type { Vehicle } from "../../types/booking";
import { bookingService } from "../../api/services/bookingService";
import {
  HOURLY_DURATION_OPTIONS,
  getHourlyOption,
} from "../../constants/hourlyBooking";
import { calculateTransferPrice } from "../../constants/transferBooking";

export const vehicles: Vehicle[] = [];

function formatDisplayDate(dateStr: string) {
  if (!dateStr) return "Add Date";

  const parts = dateStr.split("-").map(Number);
  const date =
    parts.length === 3 && parts.every((part) => !Number.isNaN(part))
      ? new Date(parts[0], parts[1] - 1, parts[2])
      : new Date(dateStr);

  if (Number.isNaN(date.getTime())) return dateStr;

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
}

function formatDisplayTime(timeStr: string) {
  if (!timeStr) return "9.00 AM";

  const [hoursStr, minutesStr = "00"] = timeStr.split(":");
  const hours = Number(hoursStr);

  if (Number.isNaN(hours)) return timeStr;

  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;

  return `${hour12}.${minutesStr.padStart(2, "0")} ${period}`;
}

function getCancellationDeadline(dateStr: string, timeStr: string) {
  if (!dateStr || !timeStr) return "";

  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes = 0] = timeStr.split(":").map(Number);

  if (![year, month, day, hours, minutes].every(Number.isFinite)) {
    return "";
  }

  const deadline = new Date(
    Date.UTC(year, month - 1, day, hours, minutes) -
      24 * 60 * 60 * 1000,
  );

  const deadlineDate = [
    deadline.getUTCFullYear(),
    String(deadline.getUTCMonth() + 1).padStart(2, "0"),
    String(deadline.getUTCDate()).padStart(2, "0"),
  ].join("-");

  const deadlineTime = [
    String(deadline.getUTCHours()).padStart(2, "0"),
    String(deadline.getUTCMinutes()).padStart(2, "0"),
  ].join(":");

  return `${formatDisplayDate(deadlineDate)} ${formatDisplayTime(deadlineTime)}`;
}

type BookStep1Props = {
  onBack?: () => void;
  onNext: (vehicle: Vehicle) => void;
  searchData: BookingSearchData;
  onSearchDataChange: (
    next:
      | BookingSearchData
      | ((current: BookingSearchData) => BookingSearchData),
  ) => void;
  servicePrice?: number;
  serviceTitle?: string;
  serviceType?: string;
};

export default function BookStep1({
  onBack,
  onNext,
  searchData,
  onSearchDataChange,
  servicePrice = 0,
  serviceTitle = "",
  serviceType = "",
}: BookStep1Props) {
  const [searchParams] = useSearchParams();

  const resolvedServicePrice =
    servicePrice > 0
      ? servicePrice
      : Number(searchParams.get("servicePrice")) || 0;

  const resolvedServiceTitle =
    serviceTitle ||
    searchParams.get("serviceTitle") ||
    (serviceType === "privatetours"
      ? "Private Tour"
      : serviceType === "daytrips"
        ? "Day Trip"
        : serviceType === "skitransfers"
          ? "Ski Transfer"
          : "Service");

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null,
  );
  const [isReturnOpen, setIsReturnOpen] = useState(false);
  const [isPassengerOpen, setIsPassengerOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<
    "from" | "to" | null
  >(null);

  const returnFieldRef = useRef<HTMLDivElement>(null);
  const routeFieldRef = useRef<HTMLDivElement>(null);
  const passengerDropdownRef = useRef<HTMLDivElement>(null);

  const currentServiceType =
    serviceType || searchParams.get("serviceType") || "";

  const isHourly = currentServiceType === "hourly";
  const isDayTrip = currentServiceType === "daytrips";
  const isTransfer = currentServiceType === "transfers";
  const isSkiTransfer = currentServiceType === "skitransfers";

  const isTwoWay =
    isTransfer && Boolean(searchData.returnDate);

  const cancellationDeadline =
    getCancellationDeadline(searchData.date, searchData.time) ||
    `${formatDisplayDate(searchData.date)} ${formatDisplayTime(searchData.time)}`;

  /*
   * For hourly bookings, distanceKm represents the INCLUDED
   * kilometre allowance for the selected number of hours.
   *
   * It is display-only and MUST NOT be used to calculate
   * the hourly vehicle price.
   */
  const hourlyDistanceKm =
    searchData.distanceKm ||
    (searchData.durationHours ? searchData.durationHours * 40 : 80);

  const transferDistanceKm = searchData.distanceKm || 0;

  const getVehiclePrice = (v: Vehicle) => {
    /*
     * HOURLY:
     * Price is strictly:
     *
     * hourlyPrice × selected duration
     *
     * The included kilometres are NOT part of the price calculation.
     */
    if (isHourly) {
      const hourlyRate = Number(v.hourlyPrice ?? 0);
      const hours =
        Number(searchData.durationHours) > 0
          ? Number(searchData.durationHours)
          : 1;

      return Math.round(hourlyRate * hours);
    }

    /*
     * DAY TRIPS:
     * Match the DayTripListing page: use dayTripPrice when provided,
     * with price as a fallback for older API responses.
     */
    if (currentServiceType === "daytrips") {
      const dayTripVehicle = v as Vehicle & { dayTripPrice?: number };
      return Math.round(Number(dayTripVehicle.dayTripPrice ?? v.price ?? 0));
    }

    /*
     * NORMAL TRANSFERS:
     * Existing distance-based pricing remains unchanged.
     */
    const oneWay = calculateTransferPrice(
      v.basePrice ?? v.price,
      v.pricePerKm ?? 0,
      transferDistanceKm,
    );

    return isTwoWay ? oneWay * 2 : oneWay;
  };

  /*
   * For Day Trips and Ski Transfers, the service price is shown
   * together with the vehicle price on the vehicle card.
   *
   * For hourly bookings, the hourly vehicle price is already the
   * complete transport price, so servicePrice must NOT be added.
   */
  const shouldShowCombinedServicePrice =
    !isHourly &&
    (currentServiceType === "daytrips" ||
      currentServiceType === "skitransfers");

  const getDisplayedVehiclePrice = (vehicle: Vehicle) => {
    const fleetPrice = getVehiclePrice(vehicle);

    return (
      fleetPrice +
      (shouldShowCombinedServicePrice ? resolvedServicePrice : 0)
    );
  };

  /*
   * minimumBookingHours means the minimum amount of
   * advance notice required before pickup.
   *
   * Example:
   * Current time: 2:00 PM
   * Vehicle requires: 2 hours
   * Pickup at 3:00 PM -> unavailable
   * Pickup at 4:00 PM -> available
   */
  const isVehicleAvailableForPickup = (vehicle: Vehicle) => {
    if (!searchData.date || !searchData.time) {
      return true;
    }

    // searchData.date is expected to be YYYY-MM-DD.
    // searchData.time is expected to be HH:mm.
    const [year, month, day] = searchData.date.split("-").map(Number);
    const [hours, minutes] = searchData.time.split(":").map(Number);

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return true;
    }

    const pickupDateTime = new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      0,
      0,
    );

    const minimumBookingHours = Math.max(
      1,
      Number(vehicle.minimumBookingHours ?? 1),
    );

    const earliestPickup = new Date(
      Date.now() + minimumBookingHours * 60 * 60 * 1000,
    );

    return pickupDateTime.getTime() >= earliestPickup.getTime();
  };

  const totalPassengers = Math.max(
    1,
    Number(searchData.adults || 0) + Number(searchData.children || 0),
  );

  const availableVehicles = vehicles.filter(
    isVehicleAvailableForPickup,
  );

  /*
   * Suitable vehicles are shown first and ordered by capacity.
   * Vehicles that cannot accommodate the selected party remain
   * visible below the suitable vehicles, but are disabled.
   */
  const sortedVehicles = [...availableVehicles].sort((a, b) => {
    const aFits = Number(a.passengers ?? 0) >= totalPassengers;
    const bFits = Number(b.passengers ?? 0) >= totalPassengers;

    // Suitable vehicles always come first.
    if (aFits && !bFits) return -1;
    if (!aFits && bFits) return 1;

    // Within each group, preserve display_order.
    return (
      Number(a.displayOrder ?? a.display_order ?? 0) -
      Number(b.displayOrder ?? b.display_order ?? 0)
    );
  });

  const suitableVehiclesCount = sortedVehicles.filter(
    (v) => Number(v.passengers ?? 0) >= totalPassengers,
  ).length;

  const selectedVehicleRaw = vehicles.find(
    (vehicle) => vehicle.id === selectedVehicleId,
  );

  /*
   * Keep selectedVehicle.price as the vehicle/fleet price only.
   *
   * Hourly:
   *   hourlyPrice × durationHours
   *
   * Normal transfer:
   *   distance-based transfer price
   *
   * Day Trip:
   *   dayTripPrice is handled by the existing day-trip flow.
   */
  const selectedVehicle = selectedVehicleRaw
    ? {
        ...selectedVehicleRaw,
        price: getVehiclePrice(selectedVehicleRaw),
      }
    : null;

  const hasSelection =
    Boolean(selectedVehicle) &&
    Number(selectedVehicle?.passengers ?? 0) >= totalPassengers;

  const departureValue: DateTimeValue | null = searchData.date
    ? {
        date: searchData.date,
        time: searchData.time || "09:00",
      }
    : null;

  const returnValue: DateTimeValue | null = searchData.returnDate
    ? {
        date: searchData.returnDate,
        time: searchData.returnTime || "09:00",
      }
    : null;

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        returnFieldRef.current &&
        !returnFieldRef.current.contains(target)
      ) {
        setIsReturnOpen(false);
      }

      if (
        routeFieldRef.current &&
        !routeFieldRef.current.contains(target)
      ) {
        setEditingLocation(null);
      }

      if (
        passengerDropdownRef.current &&
        !passengerDropdownRef.current.contains(target)
      ) {
        setIsPassengerOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsReturnOpen(false);
        setEditingLocation(null);
        setIsPassengerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    const loadVehicles = async () => {
      setLoading(true);

      try {
        const data = await bookingService.fetchVehicles();
        setVehicles(data);
      } catch (error) {
        console.error("Failed to load vehicles", error);
      } finally {
        setLoading(false);
      }
    };

    loadVehicles();
  }, []);

  /*
   * If pickup date/time changes and the currently selected
   * vehicle no longer satisfies its minimum advance notice,
   * OR if total passengers exceeds vehicle capacity,
   * automatically clear the selection.
   */
  useEffect(() => {
    if (selectedVehicleId) {
      const selected = vehicles.find(
        (vehicle) => vehicle.id === selectedVehicleId,
      );

      const isAvailable = availableVehicles.some(
        (vehicle) => vehicle.id === selectedVehicleId,
      );

      const hasCapacity =
        selected &&
        Number(selected.passengers ?? 0) >= totalPassengers;

      if (!isAvailable || !hasCapacity) {
        setSelectedVehicleId(null);
      }
    }
  }, [
    selectedVehicleId,
    searchData.date,
    searchData.time,
    totalPassengers,
    availableVehicles,
    vehicles,
  ]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    window.history.back();
  };

  const handleNext = () => {
    if (!selectedVehicle) return;

    if (Number(selectedVehicle.passengers ?? 0) < totalPassengers) {
      return;
    }

    onNext(selectedVehicle);
  };

  // Salzburg mutual boundary validation
  const isFromInSalzburg = isLocationInSalzburg({
    title: searchData.fromTitle,
    address: searchData.fromAddress,
    lat: searchData.fromLat,
    lng: searchData.fromLng,
  });

  const isToInSalzburg = isLocationInSalzburg({
    title: searchData.toTitle,
    address: searchData.toAddress,
    lat: searchData.toLat,
    lng: searchData.toLng,
  });

  // If TO is outside Salzburg, FROM must be in Salzburg.
  // For hourly driver, FROM must be in Salzburg.
  const isFromRestrictedToSalzburg =
    isHourly || (!isToInSalzburg && Boolean(searchData.toTitle));

  // If FROM is outside Salzburg, TO must be in Salzburg.
  const isToRestrictedToSalzburg =
    !isFromInSalzburg && Boolean(searchData.fromTitle);

  const handleSelectFrom = (location: LocationGeoData) => {
    onSearchDataChange((current) => ({
      ...current,
      fromTitle: location.title,
      fromAddress: location.address,
      fromLat: location.lat,
      fromLng: location.lng,
    }));

    setEditingLocation(null);
  };

  const handleSelectTo = (location: LocationGeoData) => {
    onSearchDataChange((current) => ({
      ...current,
      toTitle: location.title,
      toAddress: location.address,
      toLat: location.lat,
      toLng: location.lng,
    }));

    setEditingLocation(null);
  };

  const handleReturnConfirm = (next: DateTimeValue) => {
    onSearchDataChange((current) => ({
      ...current,
      returnDate: next.date,
      returnTime: next.time,
    }));

    setIsReturnOpen(false);
  };

  const RouteBlock = ({
    fromTitle,
    fromAddress,
    toTitle,
    toAddress,
    fromDate,
    editable = false,
    isHourlyTrip = false,
    durationHours = 2,
    distanceKm = 80,
    durationText,
  }: {
    fromTitle: string;
    fromAddress: string;
    toTitle: string;
    toAddress: string;
    fromDate?: string;
    editable?: boolean;
    isHourlyTrip?: boolean;
    durationHours?: number;
    distanceKm?: number;
    durationText?: string;
  }) => (
    <div
      ref={editable ? routeFieldRef : undefined}
      className="relative mt-5 rounded-[14px] bg-[#F7F7F7] px-4 py-4"
    >
      <div className="relative flex gap-3">
        <div className="flex w-[18px] shrink-0 flex-col items-center pt-[3px]">
          <span className="relative z-[1] h-[14px] w-[14px] rounded-full border-[3px] border-[#365E92] bg-white" />
          <span className="my-[2px] w-px flex-1 bg-[#D0D0D0]" />
        </div>

        <div className="min-w-0 flex-1 pb-4">
          <div className="flex items-start justify-between gap-3">
            <span className="font-poppins text-[10px] font-semibold uppercase tracking-[0.06em] text-[#365E92]">
              From
            </span>

            {fromDate ? (
              <span className="shrink-0 rounded-full bg-white px-2.5 py-[3px] font-poppins text-[10px] font-semibold text-[#444444]">
                {fromDate}
              </span>
            ) : null}
          </div>

          {editable && editingLocation === "from" ? (
            <ItineraryLocationSearchInput
              label="FROM"
              initialValue={fromTitle}
              placeholder="Search pickup address, hotel, or city..."
              isRestrictedToSalzburg={isFromRestrictedToSalzburg}
              onSelect={handleSelectFrom}
              onCancel={() => setEditingLocation(null)}
            />
          ) : editable ? (
            <button
              type="button"
              onClick={() =>
                setEditingLocation((current) =>
                  current === "from" ? null : "from",
                )
              }
              className="mt-1 w-full text-left"
            >
              <p className="font-host text-[15px] font-medium leading-[1.3] text-black">
                <strong className="font-semibold">{fromTitle}</strong>

                {fromAddress ? (
                  <span className="font-normal text-[#555555]">
                    {`, ${fromAddress}`}
                  </span>
                ) : null}
              </p>

              <span className="mt-1 inline-block font-poppins text-[10px] font-medium text-[#365E92]">
                Edit pickup
              </span>
            </button>
          ) : (
            <p className="mt-1 font-host text-[15px] font-medium leading-[1.3] text-black">
              <strong className="font-semibold">{fromTitle}</strong>

              {fromAddress ? (
                <span className="font-normal text-[#555555]">
                  {`, ${fromAddress}`}
                </span>
              ) : null}
            </p>
          )}
        </div>
      </div>

      {isHourlyTrip ? (
        <div className="relative flex gap-3">
          <div className="flex w-[18px] shrink-0 flex-col items-center pt-[3px]">
            <span className="relative z-[1] h-[14px] w-[14px] rotate-45 rounded-[3px] border-[3px] border-[#9BC344] bg-white" />
          </div>

          <div className="min-w-0 flex-1">
            <span className="font-poppins text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6F9A2A]">
              Duration
            </span>

            <p className="mt-1 font-host text-[15px] font-medium leading-[1.3] text-black">
              <strong className="font-semibold">
                {durationHours} Hours
              </strong>

              <span className="font-normal text-[#555555]">
                {` (${distanceKm} km included)`}
              </span>
            </p>
          </div>
        </div>
      ) : (
        <div className="relative flex gap-3">
          <div className="flex w-[18px] shrink-0 flex-col items-center pt-[3px]">
            <span className="relative z-[1] h-[14px] w-[14px] rotate-45 rounded-[3px] border-[3px] border-[#9BC344] bg-white" />
          </div>

          <div className="min-w-0 flex-1">
            <span className="font-poppins text-[10px] font-semibold uppercase tracking-[0.06em] text-[#6F9A2A]">
              To
            </span>

            {editable && !isSkiTransfer && editingLocation === "to" ? (
              <ItineraryLocationSearchInput
                label="TO"
                initialValue={toTitle}
                placeholder="Search drop-off address, hotel, or city..."
                isRestrictedToSalzburg={isToRestrictedToSalzburg}
                onSelect={handleSelectTo}
                onCancel={() => setEditingLocation(null)}
              />
            ) : editable && !isSkiTransfer ? (
              <button
                type="button"
                onClick={() =>
                  setEditingLocation((current) =>
                    current === "to" ? null : "to",
                  )
                }
                className="mt-1 w-full text-left"
              >
                <p className="font-host text-[15px] font-medium leading-[1.3] text-black">
                  <strong className="font-semibold">{toTitle}</strong>

                  {toAddress ? (
                    <span className="font-normal text-[#555555]">
                      {`, ${toAddress}`}
                    </span>
                  ) : null}
                </p>

                <span className="mt-1 inline-block font-poppins text-[10px] font-medium text-[#365E92]">
                  Edit drop-off
                </span>
              </button>
            ) : (
              <div>
                <p className="mt-1 font-host text-[15px] font-medium leading-[1.3] text-black">
                  <strong className="font-semibold">{toTitle}</strong>

                  {toAddress ? (
                    <span className="font-normal text-[#555555]">
                      {`, ${toAddress}`}
                    </span>
                  ) : null}
                </p>
                {isSkiTransfer && (
                  <span className="mt-1 inline-block font-poppins text-[10px] font-medium text-[#6F9A2A]">
                    Fixed ski destination
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!isHourlyTrip && distanceKm && distanceKm > 0 ? (
        <div className="mt-3.5 flex items-center justify-between rounded-xl border border-[#E9ECEF] bg-white p-2.5 shadow-sm">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-orange/10 text-brand-orange">
              <svg
                className="h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="6" cy="19" r="3" />
                <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
                <circle cx="18" cy="5" r="3" />
              </svg>
            </span>

            <div>
              <p className="font-poppins text-[10px] font-medium uppercase leading-none tracking-[0.02em] text-[#71717A]">
                Distance
              </p>

              <p className="mt-0.5 font-poppins text-[13px] font-bold text-black">
                {distanceKm} km
              </p>
            </div>
          </div>

          <div className="h-6 w-px bg-[#E5E9EB]" />

          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#2563EB]/10 text-[#2563EB]">
              <svg
                className="h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </span>

            <div>
              <p className="font-poppins text-[10px] font-medium uppercase leading-none tracking-[0.02em] text-[#71717A]">
                Travel Time
              </p>

              <p className="mt-0.5 font-poppins text-[13px] font-bold text-black">
                {durationText ||
                  (durationHours ? `${durationHours} hrs` : "Estimated")}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <section className="relative px-4 pb-[145px] sm:px-8 lg:px-[6vw]">
      <div className="mx-auto w-full max-w-[1450px]">
        <div className="mt-8">
          <h1 className="font-host text-[22px] font-semibold tracking-[-0.03em] text-black sm:text-[30px]">
            Select your Ride
          </h1>

          <p className="mt-2 font-poppins text-[14px] leading-[1.55] text-[#333333] sm:text-[16px]">
            Choose the perfect vehicle for your journey, tailored to your
            comfort and group size.
          </p>
        </div>

        <div className="mt-5 grid items-start gap-5 sm:gap-8 lg:grid-cols-[minmax(0,1.34fr)_minmax(410px,0.86fr)] lg:gap-10">
          {/* Left vehicle selection */}
          <div className="rounded-[22px] bg-white/90 p-3.5 shadow-[0_6px_24px_rgba(0,0,0,0.035)] sm:rounded-[30px] sm:p-7">
            <div className="inline-flex items-center gap-2 rounded-[9px] bg-[#B8D877] px-4 py-3">
              <img
                src="/assets/images/tab-transfers.png"
                alt=""
                className="h-[18px] w-[18px] object-contain"
              />

              <span className="font-host text-[15px] font-medium text-black">
                {isHourly ? "Hourly Drivers" : "Transfers"}
              </span>
            </div>

            {isHourly && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2.5 rounded-[14px] border border-[#E4E4E4] bg-[#F7F7F7] p-3">
                <div className="flex items-center gap-2">
                  <span className="font-poppins text-[11px] font-bold uppercase tracking-wider text-[#444444]">
                    Duration:
                  </span>

                  <select
                    value={searchData.durationHours || 2}
                    onChange={(e) => {
                      const hrs = Number(e.target.value);
                      const opt = getHourlyOption(hrs);
                      const km = opt ? opt.km : hrs * 40;

                      onSearchDataChange((prev) => ({
                        ...prev,
                        durationHours: hrs,
                        distanceKm: km,
                      }));
                    }}
                    className="cursor-pointer rounded-[8px] border border-[#D0D0D0] bg-white px-3 py-1.5 font-poppins text-[13px] font-semibold text-black focus:border-[#FF6A1A] focus:outline-none"
                  >
                    {HOURLY_DURATION_OPTIONS.map((opt) => (
                      <option key={opt.hours} value={opt.hours}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 rounded-[8px] border border-[#B8D877] bg-white px-3 py-[7px] font-poppins text-[11px] font-medium text-[#365E92]">
                <img
                  src="/assets/images/Frame126.png"
                  alt=""
                  className="h-[16px] w-[16px] shrink-0 object-contain"
                />
                Door-to-Door Service
              </span>

              <span className="inline-flex items-center gap-2 rounded-[8px] border border-[#D3D3D3] bg-white px-3 py-[7px] font-poppins text-[11px] font-medium text-[#365E92]">
                <img
                  src="/assets/images/Frame127.png"
                  alt=""
                  className="h-[16px] w-[16px] shrink-0 object-contain"
                />
                Driver Speaks English
              </span>

              <span className="inline-flex items-center gap-2 rounded-[8px] border border-[#D3D3D3] bg-white px-3 py-[7px] font-poppins text-[11px] font-medium text-[#365E92]">
                <img
                  src="/assets/images/Frame128.png"
                  alt=""
                  className="h-[16px] w-[16px] shrink-0 object-contain"
                />
                Flexible Stops
              </span>
            </div>

            <div className="mt-4 flex w-full flex-col gap-3">
              <div className="flex items-center justify-between pb-1">
                <h3 className="font-host text-[17px] font-semibold text-black sm:text-[19px]">
                  Available Fleets
                </h3>

                <span className="rounded-full bg-[#EAEAEA] px-3 py-1 font-poppins text-[11px] font-medium text-[#444444]">
                  {totalPassengers}{" "}
                  {totalPassengers === 1 ? "Passenger" : "Passengers"}
                </span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#9BC344] border-t-transparent" />
                </div>
              ) : vehicles.length === 0 ? (
                <p className="py-4 text-center text-[14px] text-gray-500">
                  No vehicles found.
                </p>
              ) : availableVehicles.length === 0 ? (
                <p className="py-4 text-center text-[14px] text-gray-500">
                  No vehicles are available for the selected pickup time.
                </p>
              ) : (
                <>
                  {suitableVehiclesCount === 0 && (
                    <div className="rounded-[14px] border border-[#FCD34D] bg-[#FFFBEB] p-4 text-[13px] text-[#92400E]">
                      <p className="font-semibold">
                        No fleet available for {totalPassengers} passengers
                      </p>

                      <p className="mt-1 text-[12px] text-[#B45309]">
                        All available vehicles seat fewer than{" "}
                        {totalPassengers} passengers. Fleets below are shown
                        faded as they cannot accommodate your party.
                      </p>
                    </div>
                  )}

                  {sortedVehicles.map((vehicle, index) => {
                    const isSelected = vehicle.id === selectedVehicleId;

                    const isEnoughCapacity =
                      Number(vehicle.passengers ?? 0) >= totalPassengers;

                    return (
                      <div key={vehicle.id} className="w-full">
                        {index === suitableVehiclesCount &&
                          suitableVehiclesCount > 0 && (
                            <div className="my-2.5 flex items-center gap-3 py-1">
                              <div className="h-px flex-1 bg-[#E0E0E0]" />

                              <span className="font-poppins text-[10px] font-semibold uppercase tracking-wider text-[#888888] sm:text-[11px]">
                                Other fleets (
                                {sortedVehicles.length -
                                  suitableVehiclesCount}{" "}
                                seat fewer than {totalPassengers} passengers)
                              </span>

                              <div className="h-px flex-1 bg-[#E0E0E0]" />
                            </div>
                          )}

                        <button
                          type="button"
                          disabled={!isEnoughCapacity}
                          aria-disabled={!isEnoughCapacity}
                          aria-pressed={isSelected}
                          onClick={() => {
                            if (!isEnoughCapacity) return;
                            setSelectedVehicleId(vehicle.id);
                          }}
                          className={`group grid w-full shrink-0 grid-cols-[68px_minmax(0,1fr)_72px] items-center gap-1.5 overflow-hidden rounded-[16px] border px-2 py-3 text-left transition-all duration-200 min-[375px]:grid-cols-[72px_minmax(0,1fr)_72px] min-[375px]:gap-2 min-[420px]:grid-cols-[100px_minmax(0,1fr)_88px] min-[420px]:gap-3 min-[420px]:px-3 min-[420px]:py-4 sm:min-h-[180px] sm:grid-cols-[160px_minmax(0,1fr)_116px] sm:px-4 lg:min-h-[120px] lg:grid-cols-[120px_minmax(0,1fr)_90px] lg:gap-2 lg:px-3 lg:py-2 ${
                            !isEnoughCapacity
                              ? "cursor-not-allowed border-[#E4E4E4] bg-[#F8F9FA] opacity-45 shadow-none hover:border-[#E4E4E4] hover:shadow-none"
                              : isSelected
                                ? "border-[#9BC344] bg-[#EAF6D4] shadow-[0_5px_15px_rgba(155,195,68,0.18)]"
                                : "border-[#D9D9D9] bg-white hover:shadow-[0_5px_15px_rgba(0,0,0,0.07)]"
                          }`}
                        >
                          <div className="flex h-[72px] w-full items-center justify-center min-[420px]:h-[90px] sm:h-[104px] lg:h-[70px]">
                            <img
                              src={vehicle.image}
                              alt={vehicle.name}
                              className={`max-h-[62px] w-full object-contain min-[420px]:max-h-[78px] sm:max-h-[90px] lg:max-h-[60px] ${
                                !isEnoughCapacity ? "opacity-60" : ""
                              }`}
                            />
                          </div>

                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <p className="flex items-center gap-1.5 font-host text-[11px] font-medium text-[#222222] sm:text-[13px] lg:text-[11px]">
                                <span
                                  className={`h-[10px] w-[10px] shrink-0 rounded-full ${
                                    !isEnoughCapacity
                                      ? "bg-[#B0B0B0]"
                                      : "bg-[#C7C7C7]"
                                  }`}
                                />

                                {vehicle.category}
                              </p>

                              {!isEnoughCapacity && (
                                <span className="rounded-full bg-[#FEE2E2] px-2 py-0.5 font-poppins text-[9px] font-semibold text-[#DC2626] sm:text-[10px]">
                                  Max {vehicle.passengers} seats
                                </span>
                              )}
                            </div>

                            <h2
                              className={`mt-1 truncate font-host text-[15px] font-semibold leading-tight min-[420px]:text-[18px] sm:text-[20px] lg:text-[16px] ${
                                !isEnoughCapacity
                                  ? "text-[#555555]"
                                  : "text-black"
                              }`}
                            >
                              {vehicle.name}
                            </h2>

                            <div className="mt-2 flex items-center gap-3 text-[#5D5D5D]">
                              <span
                                className={`flex items-center gap-1 font-host text-[13px] lg:text-[12px] ${
                                  !isEnoughCapacity
                                    ? "font-semibold text-[#DC2626]"
                                    : ""
                                }`}
                              >
                                <img
                                  src="/assets/images/icon-adult.png"
                                  alt=""
                                  className={`h-[15px] w-[15px] object-contain lg:h-[13px] lg:w-[13px] ${
                                    !isEnoughCapacity
                                      ? "opacity-90"
                                      : "opacity-70"
                                  }`}
                                />

                                {vehicle.passengers}
                                {!isEnoughCapacity ? " max" : ""}
                              </span>

                              <span className="flex items-center gap-1 font-host text-[13px] lg:text-[12px]">
                                <img
                                  src="/assets/images/icon-child.png"
                                  alt=""
                                  className="h-[15px] w-[15px] object-contain opacity-70 lg:h-[13px] lg:w-[13px]"
                                />

                                {vehicle.luggage}
                              </span>
                            </div>
                          </div>

                          <div
                            className={`flex min-h-[64px] flex-col items-center justify-center rounded-[13px] px-1.5 transition-colors duration-200 min-[420px]:min-h-[80px] min-[420px]:px-2 sm:min-h-[88px] lg:min-h-[60px] ${
                              !isEnoughCapacity
                                ? "bg-[#ECECEC] text-[#777777]"
                                : "bg-[#F5FBE7]"
                            }`}
                          >
                            <span
                              className={`font-host text-[18px] font-semibold leading-none min-[420px]:text-[22px] sm:text-[25px] lg:text-[18px] ${
                                !isEnoughCapacity
                                  ? "text-[#777777]"
                                  : "text-black"
                              }`}
                            >
                              €{Math.round(getDisplayedVehiclePrice(vehicle))}
                            </span>

                            <span
                              className={`mt-1.5 whitespace-nowrap font-poppins text-[8px] font-medium min-[420px]:mt-2 min-[420px]:text-[9px] sm:text-[10px] lg:text-[9px] ${
                                !isEnoughCapacity
                                  ? "font-semibold text-[#888888]"
                                  : "text-black"
                              }`}
                            >
                              {!isEnoughCapacity
                                ? "Not enough seats"
                                : "Reserve Now ›"}
                            </span>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          {/* Right booking summary */}
          <aside className="rounded-[24px] bg-white p-4 shadow-[0_5px_20px_rgba(0,0,0,0.12)] sm:rounded-[30px] sm:p-7 lg:sticky lg:top-5 lg:self-start lg:rounded-[24px] lg:p-5">
{isDayTrip && (
  <div className="mb-4 rounded-xl border border-[#E5E7EB] bg-[#F8F9FA] p-3.5">
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#E5EED5] text-[#527A22]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M17 2l4 4-4 4" />
          <path d="M3 6h18" />
          <path d="M7 22l-4-4 4-4" />
          <path d="M21 18H3" />
        </svg>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-host text-[15px] font-semibold leading-snug text-black">
            Private Round-Trip Excursion
          </h3>

          <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF3DF] px-2 py-1 font-poppins text-[9px] font-semibold tracking-wide text-[#527A22]">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            ROUND TRIP INCLUDED
          </span>
        </div>

        <p className="mt-1.5 font-poppins text-[11px] leading-[1.7] text-[#555555]">
          Travel exclusively with a private driver
          to your destination and back, for a
          seamless round-trip experience.
        </p>
      </div>
    </div>
  </div>
)}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {!isHourly && (
                <span className="rounded-full bg-[#365E92] px-4 py-[8px] font-poppins text-[11px] font-semibold text-white sm:px-5 sm:py-[9px] sm:text-[12px]">
                  {isTwoWay ? "TWO WAY" : "ONE WAY"}
                </span>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <div
                  ref={passengerDropdownRef}
                  className="relative"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setIsPassengerOpen((prev) => !prev)
                    }
                    className="flex items-center gap-1.5 rounded-full bg-[#F2F2F2] px-3 py-[7px] font-poppins text-[11px] font-medium text-black transition-colors hover:bg-[#E5E5E5]"
                    title="Click to edit passengers"
                  >
                    <img
                      src="/assets/images/icon-adult.png"
                      alt=""
                      className="h-[14px] w-[14px] object-contain"
                    />

                    <span>
                      {totalPassengers}{" "}
                      {totalPassengers === 1 ? "Guest" : "Guests"}
                    </span>

                    <span className="ml-0.5 text-[10px] font-semibold text-[#365E92] underline">
                      Edit
                    </span>
                  </button>

                  {isPassengerOpen && (
                    <div className="absolute right-0 top-full z-[130] mt-2 w-[240px] rounded-[16px] border border-[#E0E0E0] bg-white p-4 shadow-[0_10px_25px_rgba(0,0,0,0.18)]">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-host text-[14px] font-semibold text-black">
                              Adults
                            </p>

                            <p className="text-[11px] text-[#888888]">
                              Age 12+
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={searchData.adults <= 1}
                              onClick={() =>
                                onSearchDataChange((prev) => ({
                                  ...prev,
                                  adults: Math.max(
                                    1,
                                    prev.adults - 1,
                                  ),
                                }))
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAEAEA] text-[16px] font-semibold text-black disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              −
                            </button>

                            <span className="w-5 text-center font-host text-[14px] font-bold text-black">
                              {searchData.adults}
                            </span>

                            <button
                              type="button"
                              disabled={
                                searchData.adults +
                                  searchData.children >=
                                16
                              }
                              onClick={() =>
                                onSearchDataChange((prev) => ({
                                  ...prev,
                                  adults: prev.adults + 1,
                                }))
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAEAEA] text-[16px] font-semibold text-black disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-host text-[14px] font-semibold text-black">
                              Children
                            </p>

                            <p className="text-[11px] text-[#888888]">
                              Age 0–12
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={searchData.children <= 0}
                              onClick={() =>
                                onSearchDataChange((prev) => ({
                                  ...prev,
                                  children: Math.max(
                                    0,
                                    prev.children - 1,
                                  ),
                                }))
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAEAEA] text-[16px] font-semibold text-black disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              −
                            </button>

                            <span className="w-5 text-center font-host text-[14px] font-bold text-black">
                              {searchData.children}
                            </span>

                            <button
                              type="button"
                              disabled={
                                searchData.adults +
                                  searchData.children >=
                                16
                              }
                              onClick={() =>
                                onSearchDataChange((prev) => ({
                                  ...prev,
                                  children: prev.children + 1,
                                }))
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAEAEA] text-[16px] font-semibold text-black disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setIsPassengerOpen(false)}
                          className="mt-2 w-full rounded-[8px] bg-black py-1.5 font-poppins text-[11px] font-semibold text-white hover:bg-[#222222]"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <span className="flex items-center gap-1 rounded-full bg-[#F2F2F2] px-3 py-[7px] font-poppins text-[11px] font-medium text-black">
                  <img
                    src="/assets/images/icon-child.png"
                    alt=""
                    className="h-[14px] w-[14px] object-contain"
                  />

                  {searchData.extraBags}
                </span>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <span className="flex items-center gap-1.5 rounded-full bg-[#EEEEEE] px-3 py-[7px] font-poppins text-[11px] font-medium text-black">
                <span className="text-[18px] leading-none">→</span>

                <img
                  src="/assets/images/image169.png"
                  alt=""
                  className="h-[12px] w-[12px] object-contain"
                />

                 {formatDisplayDate(
                    searchData.date
                  )}

                  {searchData.time
                    ? ` · ${formatDisplayTime(
                        searchData.time
                      )}`
                    : ''}
              </span>
            </div>

            <RouteBlock
              fromTitle={searchData.fromTitle}
              fromAddress={searchData.fromAddress}
              toTitle={searchData.toTitle}
              toAddress={searchData.toAddress}
              fromDate={formatDisplayDate(searchData.date)}
              editable
              isHourlyTrip={isHourly}
              durationHours={searchData.durationHours || 2}
              distanceKm={
                searchData.distanceKm || hourlyDistanceKm
              }
              durationText={searchData.durationText}
            />

            {isTwoWay ? (
              <>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                  <span className="flex min-w-0 max-w-full items-center gap-1.5 rounded-full bg-[#EEEEEE] px-3 py-[7px] font-poppins text-[11px] font-medium text-black">
                    <span className="text-[18px] leading-none">⇄</span>

                    <img
                      src="/assets/images/image169.png"
                      alt=""
                      className="h-[12px] w-[12px] shrink-0 object-contain"
                    />

                    <span className="truncate">
                      {formatDisplayDate(searchData.returnDate)}

                      {searchData.returnTime
                        ? ` · ${formatDisplayTime(
                            searchData.returnTime,
                          )}`
                        : ""}
                    </span>
                  </span>

                  <button
                    type="button"
                    onClick={() => setIsReturnOpen(true)}
                    className="shrink-0 font-host text-[15px] font-medium text-[#365E92]"
                  >
                    Edit
                  </button>
                </div>

                <RouteBlock
                  fromTitle={searchData.toTitle}
                  fromAddress={searchData.toAddress}
                  toTitle={searchData.fromTitle}
                  toAddress={searchData.fromAddress}
                  fromDate={formatDisplayDate(
                    searchData.returnDate,
                  )}
                  distanceKm={
                    searchData.distanceKm || hourlyDistanceKm
                  }
                  durationText={searchData.durationText}
                />
              </>
            ) : isTransfer ? (
              <div
                ref={returnFieldRef}
                className="relative mt-5"
              >
                <button
                  type="button"
                  onClick={() =>
                    setIsReturnOpen((current) => !current)
                  }
                  className="flex min-h-[74px] w-full items-center justify-between rounded-[13px] border border-[#CECECE] bg-[#F0F0F0] px-4 py-3 text-left transition-colors duration-200 hover:bg-[#EAEAEA]"
                >
                  <span>
                    <span className="block font-poppins text-[10px] font-medium uppercase leading-none text-[#2E2E2E]">
                      Return
                    </span>

                    <span className="mt-2 block font-host text-[15px] font-semibold leading-none text-black">
                      Add Return
                    </span>
                  </span>

                  <span className="text-[25px] font-light leading-none text-black">
                    +
                  </span>
                </button>

                {isReturnOpen && (
                  <DateTimePicker
                    value={returnValue}
                    minDateTime={departureValue}
                    onCancel={() => setIsReturnOpen(false)}
                    onConfirm={handleReturnConfirm}
                  />
                )}
              </div>
            ) : null}

            {isTwoWay && isReturnOpen && (
              <div
                ref={returnFieldRef}
                className="relative mt-3"
              >
                <DateTimePicker
                  value={returnValue}
                  minDateTime={departureValue}
                  onCancel={() => setIsReturnOpen(false)}
                  onConfirm={handleReturnConfirm}
                />
              </div>
            )}

            <div className="mt-5 lg:mt-4">
              <h3 className="font-host text-[23px] font-semibold text-black lg:text-[20px]">
                Price details
              </h3>

              <div className="mt-3 flex justify-between">
                <span className="font-host text-[15px] text-black">
                  Transport ({selectedVehicle?.name || "Fleet"})
                </span>

                <span className="font-host text-[19px] text-black">
                  €{currentServiceType === "daytrips" && selectedVehicleRaw
                    ? Math.round(getDisplayedVehiclePrice(selectedVehicleRaw))
                    : selectedVehicle?.price ?? 0}
                </span>
              </div>

              {!isHourly &&
                currentServiceType !== "daytrips" &&
                resolvedServicePrice > 0 && (
                <div className="mt-3 flex justify-between">
                  <span className="font-host text-[15px] text-black">
                    {resolvedServiceTitle}
                  </span>

                  <span className="font-host text-[19px] text-black">
                    €{resolvedServicePrice}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between rounded-[10px] bg-[#EEEEEE] px-4 py-3 lg:py-2.5">
              <span className="font-host text-[22px] font-semibold text-black lg:text-[18px]">
                Total
              </span>

              <div className="rounded-[9px] bg-[#C9E589] px-4 py-2 text-right lg:px-3 lg:py-1.5">
                <span className="block font-host text-[25px] font-semibold text-black lg:text-[20px]">
                  €
                  {currentServiceType === "daytrips" && selectedVehicleRaw
                    ? Math.round(getDisplayedVehiclePrice(selectedVehicleRaw))
                    : (selectedVehicle
                        ? selectedVehicle.price
                        : 0) +
                      (isHourly
                        ? 0
                        : selectedVehicle
                          ? resolvedServicePrice
                          : 0)}
                </span>

                <span className="font-poppins text-[10px] text-black lg:text-[9px]">
                  Reserve Now
                </span>
              </div>
            </div>

            {/* <div className="mt-4 flex items-center justify-center gap-2 rounded-[6px] bg-[#F1F1F1] px-3 py-2">
              <span className="flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full bg-white text-[10px] text-black">
                ✓
              </span>

              <span className="font-poppins text-[10px] text-black sm:text-[11px]">
                Free cancellation until {cancellationDeadline}
              </span>
            </div> */}
            <div className="mt-4 flex items-start gap-2.5 rounded-[12px] border border-[#CCE5CC] bg-[#F7FDF7] p-3">
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mt-0.5 flex-shrink-0 text-green-700"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>

  <span className="font-host text-[13px] font-semibold leading-normal text-green-800">
    Free cancellation until {cancellationDeadline}
  </span>
</div>
          </aside>
        </div>
      </div>

      <div className="fixed bottom-3 left-1/2 z-[100] w-[calc(100%-1.5rem)] -translate-x-1/2 rounded-[18px] border border-[#ECECEC] bg-white/95 px-3 py-3 shadow-[0_5px_22px_rgba(0,0,0,0.14)] backdrop-blur-md sm:bottom-4 sm:w-[88vw] sm:px-10 sm:py-5 lg:w-[68vw] lg:max-w-[1120px]">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full bg-black px-4 font-poppins text-[12px] font-medium text-white sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7"
          >
            Back
          </button>

          <button
            type="button"
            onClick={handleNext}
            disabled={!hasSelection}
            className="flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full bg-[#FF6B2C] px-4 font-poppins text-[12px] font-medium text-white transition-transform duration-200 enabled:hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-[#D0D0D0] disabled:text-[#8A8A8A] disabled:hover:translate-y-0 sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}